import { FiCheckCircle } from "react-icons/fi"
import Header from "../../component/header/header"
import "./QuestionPreview.style.scss"
import { useEffect, useState } from "react"
import { editBulkQuestions, previewBulkQuestions } from "../../api/question"
import { useNavigate, useParams } from "react-router-dom"
import toast from "react-hot-toast"
import { useNotification } from "../../Context/Notification.context"
import { listSubject } from "src/api/subject"
import { normalizeTaxonomyItems } from "../../utils/subjectTaxonomy"
import QuestionCard from "./QuestionCard"
import { useQuestionData } from "./useQuestionData"
import {
  BulkQuestionData,
  PreviewSubjectOption,
  ProcessedQuestionContent,
} from "./types"
import { errorMessageShow, LoaderComponent } from "../../utils/Helper"

const resolveSelectValue = (selected: unknown) => {
  if (selected == null) return ""
  if (typeof selected === "string") return selected
  if (typeof selected === "object" && "value" in selected) {
    return String((selected as { value?: string }).value ?? "")
  }
  return ""
}

const QuestionPreview = () => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { notifications, setNotifications } = useNotification()
  const [subjects, setSubjects] = useState<PreviewSubjectOption[]>([])
  const [questionValidationErrors, setQuestionValidationErrors] = useState<
    { index: number; message: string }[]
  >([])
  const [data, setData] = useState<BulkQuestionData>({
    subject: "",
    topic: "",
    complexity: "",
    grade: "",
    documents: [],
    bulk_session_id: "",
  })

  const { bulk_session_id } = useParams()
  const navigation = useNavigate()
  const { processedContent, updateContent } = useQuestionData(data)

  const handleRevertContent = (
    index: number,
    originalContent: ProcessedQuestionContent,
  ) => {
    const updatedContent = [...processedContent]
    updatedContent[index] = originalContent
    updateContent(updatedContent)
  }

  const fetchSubjects = async () => {
    try {
      const res = await listSubject(1, "", 10000)
      const subjects = res.data.data.subjects.map((item: any) => ({
        label: item.title,
        value: item.id,
        topics: normalizeTaxonomyItems(item.topics || []),
        chapters: normalizeTaxonomyItems(item.chapters || []),
      }))
      setSubjects(subjects)
    } catch (error) {
      console.error("Error fetching subjects:", error)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!bulk_session_id) return

      setLoading(true)
      try {
        const [questionsRes] = await Promise.all([
          previewBulkQuestions(bulk_session_id),
          fetchSubjects(),
        ])
        setData(questionsRes.data?.data)
      } catch (error: any) {
        console.error("Error fetching data:", error)
        errorMessageShow(
          error?.response?.data?.message ||
            "Unable to open this bulk upload preview."
        )
        navigation("/questions")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [bulk_session_id])

  const notify = () =>
    toast.success("Your question has been saved successfully.", {
      icon: <FiCheckCircle color={"var(--success-600)"} size={58} />,
    })

  const handleAddQuestion = async () => {
    setSubmitting(true)
    setQuestionValidationErrors([]) // Clear previous errors

    const errors: { index: number; message: string }[] = []
    processedContent.forEach((item, idx) => {
      if (!item.topic) {
        errors.push({ index: idx, message: "Chapter/Topic is required." })
      }
    })

    if (errors.length > 0) {
      setQuestionValidationErrors(errors)
      setSubmitting(false)
      return
    }

    const submitData = {
      bulk_session_id,
      documents: processedContent.map((item) => ({
        ...item,
        subject: item.subject,
        grade: item.grade,
        topic: item.topic_id || item.topic,
      })),
    }

    try {
      await editBulkQuestions(submitData)
      notify()

      const updatedNotifications = notifications.filter(
        (item) => item.bulk_session_id !== bulk_session_id,
      )
      setNotifications(updatedNotifications)
      navigation("/questions")
    } catch (error) {
      console.error("Error saving questions:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleContentChange = (
    name: string,
    selected: unknown,
    index: number,
  ) => {
    const updatedContent = processedContent.map((item, idx) => {
      if (idx === index) {
        if (name === "subject") {
          return {
            ...item,
            subject: resolveSelectValue(selected),
            topic: "",
          }
        }

        if (name === "topic") {
          if (
            typeof selected === "object" &&
            selected !== null &&
            "value" in selected
          ) {
            const option = selected as { value: string; label?: string }
            return {
              ...item,
              topic_id: option.value,
              topic: option.label ?? option.value,
            }
          }
          return {
            ...item,
            topic: resolveSelectValue(selected),
            topic_id: resolveSelectValue(selected),
          }
        }

        if (name === "complexity") {
          return {
            ...item,
            complexity: resolveSelectValue(selected),
          }
        }

        return { ...item, [name]: selected }
      }
      return item
    })

    updateContent(updatedContent)
  }

  return (
    <div className='content question_preview'>
      <Header
        buttonText={submitting ? "Adding Question..." : "Add these Questions"}
        pageTitle='Question Finalization'
        onClick={!submitting ? handleAddQuestion : undefined}
      />
      <div className='container'>
        {loading ? (
          <LoaderComponent label='Loading preview…' />
        ) : processedContent.length === 0 ? (
          <div>Processing content...</div>
        ) : (
          <>
            {data.documents.map((document, index) => {
              const content = processedContent[index]
              if (!content) {
                console.warn(`Missing content for index ${index}`, {
                  documentsLength: data.documents.length,
                  processedContentLength: processedContent.length,
                  index,
                })
                return null
              }

              return (
                <QuestionCard
                  key={index}
                  document={document}
                  content={content}
                  subjects={subjects}
                  index={index + 1} // Display index (1-based)
                  onChange={(name, selected) =>
                    handleContentChange(name, selected, index)
                  } // Data index (0-based)
                  onRevert={handleRevertContent}
                  questionValidationErrors={questionValidationErrors}
                />
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}

export default QuestionPreview
